import { useParams, Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Clock, BookOpen, Users, ArrowRight, CheckCircle2, ArrowLeft, MapPin, Phone } from 'lucide-react';
import SEO from '../components/SEO';
import { COURSE_DETAILS } from '../data/courseDetails';
import { buildCourseSchema, buildCourseFaqSchema } from '../data/courseSchemaBuilders';

const CourseDetail = () => {
  const params = useParams();
  const slug = params.slug;
  const course = COURSE_DETAILS[slug];

  if (!course) return <Navigate to="/courses" replace />;

  const name = course.name;
  const h1 = course.h1;
  const metaTitle = course.metaTitle;
  const metaDescription = course.metaDescription;
  const duration = course.duration;
  const level = course.level;
  const intro = course.intro;
  const covers = course.covers;
  const who = course.who;
  const outcomes = course.outcomes;
  const faqs = course.faqs;

  const canonicalPath = '/courses/' + course.slug;
  const cleanTitle = metaTitle.replace(' — Genius Academy', '');
  const kw = name + ' Course in Kalaburagi, ' + name + ' Course in Gulbarga, ' + name + ' classes, Genius Academy ' + name;
  const courseSchema = buildCourseSchema(course);
  const faqJson = JSON.stringify(buildCourseFaqSchema(course));

  return (
    <div className="pt-16 md:pt-20">
      <SEO
        title={cleanTitle}
        description={metaDescription}
        path={canonicalPath}
        keywords={kw}
        jsonLd={courseSchema}
        breadcrumbs={[
          { name: 'Home', path: '/' },
          { name: 'Courses', path: '/courses' },
          { name: name, path: canonicalPath },
        ]}
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: faqJson }} />

      <section className="bg-gradient-to-br from-slate-50 to-cyan-50 py-14 md:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav aria-label="Breadcrumb" className="text-sm text-slate-500 mb-6" data-testid="breadcrumb">
            <Link to="/" className="hover:text-cyan-600">Home</Link>
            <span className="mx-2">/</span>
            <Link to="/courses" className="hover:text-cyan-600">Courses</Link>
            <span className="mx-2">/</span>
            <span className="text-slate-700 font-medium">{name}</span>
          </nav>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <span className="inline-block bg-cyan-100 text-cyan-700 px-3 py-1 rounded-full text-xs font-semibold mb-4">
              Onsite Computer Training in Kalaburagi
            </span>
            <h1 className="text-3xl md:text-5xl font-bold tracking-tight text-slate-900 mb-4" data-testid="course-h1">
              {h1}
            </h1>
            <p className="text-base md:text-lg text-slate-600 leading-relaxed max-w-3xl">
              {intro}
            </p>
            <div className="flex flex-wrap gap-3 md:gap-4 mt-6">
              <span className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-full px-4 py-2 text-sm font-medium text-slate-700">
                <Clock size={16} className="text-cyan-600" />
                Duration: {duration}
              </span>
              <span className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-full px-4 py-2 text-sm font-medium text-slate-700">
                <BookOpen size={16} className="text-cyan-600" />
                Level: {level}
              </span>
              <span className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-full px-4 py-2 text-sm font-medium text-slate-700">
                <Users size={16} className="text-cyan-600" />
                1:1 Accountability
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-14 md:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-10">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">What You'll Learn</h2>
            <ul className="space-y-2.5">
              {covers.map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-slate-700">
                  <CheckCircle2 className="w-5 h-5 text-cyan-600 flex-shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">Who Should Join</h2>
            <ul className="space-y-2.5 mb-8">
              {who.map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-slate-700">
                  <span className="w-1.5 h-1.5 rounded-full bg-cyan-500 mt-2.5 flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">Career Outcomes</h2>
            <ul className="space-y-2.5">
              {outcomes.map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-slate-700">
                  <span className="w-1.5 h-1.5 rounded-full bg-orange-500 mt-2.5 flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
            Why Learn {name} at Genius Academy?
          </h2>
          <p className="text-slate-600 leading-relaxed mb-4">
            At Genius Academy, you're not lost in a batch of 40+ students. We deliberately keep our batches small so
            every learner gets personal attention, real feedback and enough hands-on time in the computer lab. Our 1:1
            accountability model means we track your progress daily — you don't move ahead until you've genuinely
            understood the module you're on.
          </p>
          <p className="text-slate-600 leading-relaxed mb-4">
            You learn on real computers in a dedicated lab environment, not on shared notebooks. Every session mixes
            focused instruction with active practice so the skill sticks. Along with the course, students receive
            placement assistance, career guidance, resume building support and interview preparation.
          </p>
          <p className="text-slate-500 text-sm leading-relaxed">
            <strong>Placement Disclaimer:</strong> Placement assistance is provided to eligible students. Final job
            selection depends on the student's skills, interview performance, employer requirements, and available
            opportunities.
          </p>
        </div>
      </section>

      <section className="py-14 md:py-20" aria-labelledby="course-faq">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 id="course-faq" className="text-2xl md:text-3xl font-bold text-slate-900 mb-6">
            Frequently Asked Questions
          </h2>
          <div className="space-y-3">
            {faqs.map((item, i) => (
              <details
                key={i}
                className="group bg-white border border-slate-100 rounded-2xl shadow-sm open:shadow-md open:border-cyan-200 transition-all"
              >
                <summary className="cursor-pointer list-none flex items-center justify-between p-5 font-semibold text-slate-900">
                  <span>{item.q}</span>
                  <span className="ml-4 text-cyan-600 group-open:rotate-45 transition-transform text-2xl leading-none" aria-hidden="true">+</span>
                </summary>
                <div className="px-5 pb-5 text-slate-600 leading-relaxed">
                  <p>{item.a}</p>
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20 bg-gradient-to-br from-cyan-600 to-blue-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Start the {name} Course?
          </h2>
          <p className="text-base md:text-lg opacity-90 mb-8 max-w-2xl mx-auto">
            Book a free demo class or call us to know the next batch timings.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-white text-cyan-600 hover:bg-slate-50 rounded-full px-8 py-4 font-bold shadow-lg transition-all"
              data-testid="course-detail-cta-book"
            >
              Book Free Demo <ArrowRight className="w-5 h-5" />
            </Link>
            <a
              href="tel:9164998997"
              className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-white text-white hover:bg-white/10 rounded-full px-8 py-4 font-bold transition-all"
              data-testid="course-detail-cta-call"
            >
              <Phone className="w-5 h-5" /> Call 9164998997
            </a>
          </div>
          <p className="text-sm opacity-80 mt-6 flex items-center justify-center gap-2">
            <MapPin size={14} /> Darshan Orchid Building, MSK Mill Rd, Kalaburagi 585103
          </p>
          <div className="mt-8">
            <Link
              to="/courses"
              className="inline-flex items-center gap-2 text-white/90 hover:text-white text-sm underline underline-offset-4"
              data-testid="course-detail-back-to-courses"
            >
              <ArrowLeft className="w-4 h-4" /> Back to all courses
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CourseDetail;
